import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Button } from "../ui/button";
import { Loader2, FileText, Zap } from "lucide-react";
import { useLang } from "../../lib/i18n";
import { accountApi } from "../../lib/api";

const scrollToContact = () => document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" });

export const MyQuotesModal = ({ open, onOpenChange }) => {
  const { t } = useLang();
  const a = t.account;
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    accountApi.get("/account/quotes")
      .then(({ data }) => setQuotes(Array.isArray(data) ? data : []))
      .catch(() => setQuotes([]))
      .finally(() => setLoading(false));
  }, [open]);

  const fmtDate = (iso) => {
    try { return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }); }
    catch { return ""; }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-charcoal border-white/10 text-offwhite sm:max-w-lg" data-testid="my-quotes-modal">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl tracking-tight">{a.quotesTitle}</DialogTitle>
          <DialogDescription className="text-white/50">{a.quotesSub}</DialogDescription>
        </DialogHeader>

        <div className="mt-2 max-h-[60vh] overflow-y-auto space-y-3">
          {loading ? (
            <div className="flex items-center justify-center py-10 text-white/40" data-testid="quotes-loading">
              <Loader2 className="animate-spin" />
            </div>
          ) : quotes.length === 0 ? (
            <div className="text-center py-10" data-testid="quotes-empty">
              <FileText className="mx-auto text-white/25 mb-3" size={32} />
              <p className="text-white/50 mb-5">{a.quotesEmpty}</p>
              <Button
                onClick={() => { onOpenChange(false); scrollToContact(); }}
                data-testid="quotes-empty-cta"
                className="rounded-full bg-lime2 text-charcoal hover:bg-white font-semibold"
              >
                <Zap size={15} className="mr-1.5" /> {a.quotesEmptyCta}
              </Button>
            </div>
          ) : (
            quotes.map((q, i) => (
              <div key={q.id || i} data-testid="quote-row"
                   className="rounded-xl border border-white/10 bg-black/40 p-4 flex items-center justify-between gap-3">
                <div>
                  <p className="font-display font-bold text-lime2">
                    {q.model ? `Gesung ${q.model}` : (q.use_case || "Quote")}
                  </p>
                  <p className="text-xs text-white/45 mt-0.5">{a.quoteDate}: {fmtDate(q.created_at)}</p>
                </div>
                <div className="text-right">
                  {q.local_price && <p className="font-semibold">{q.local_price}</p>}
                  <span className="inline-block mt-1 text-[11px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald2/20 text-lime2">
                    {q.status || "new"}
                  </span>
                </div>
              </div>
            ))
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
